import { FC, memo, useMemo } from 'react';

import { EntityTypeDropdown } from '@/components/nodeGraph/EntityTypeDropdown';
import { ClusterByEntityOptions } from '@/components/nodeGraph/utils/constants';
import { GraphEntityType } from '@/components/nodeGraph/utils/types';

interface ClusterByDropdownProps {
  clusterByEntity: GraphEntityType;
  setClusterByEntity: (entityType: GraphEntityType) => void;
  onClearSelection?: () => void;
}

/**
 * ClusterByDropdown - Entity Type Selection Component
 *
 * Provides a dropdown selector for choosing which entity type to display
 * in the NodeGraph visualization. This component is designed to be placed
 * in the modal panel with right-aligned layout.
 *
 * Features:
 * - Entity type selection (Assets, Risks, Technologies, AD Domains, Seeds)
 * - Conditional display of AD Domains based on AD object existence
 * - Clear selection on entity change
 * - Custom dropdown with configurable positioning
 */
const ClusterByDropdownComponent: FC<ClusterByDropdownProps> = ({
  clusterByEntity,
  setClusterByEntity,
  onClearSelection,
}) => {
  // Filter ClusterByEntityOptions based on AD existence
  const visibleClusterOptions = useMemo(() => {
    // Return all available clustering options
    return ClusterByEntityOptions;
  }, []);

  return (
    <div className="flex items-center justify-end gap-2">
      <label className="text-chariot-text-primary text-sm font-medium whitespace-nowrap">
        Select Entity Type
      </label>
      <EntityTypeDropdown
        options={visibleClusterOptions}
        selected={clusterByEntity}
        onSelect={value => {
          setClusterByEntity(value as GraphEntityType);
          onClearSelection?.();
        }}
        dropdownWidth={200}
        dropdownHeight={300}
        dropdownPosition="bottom-right"
      />
    </div>
  );
};

/**
 * Memoized ClusterByDropdown to prevent unnecessary re-renders
 */
export const ClusterByDropdown = memo(
  ClusterByDropdownComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.clusterByEntity === nextProps.clusterByEntity &&
      prevProps.setClusterByEntity === nextProps.setClusterByEntity &&
      prevProps.onClearSelection === nextProps.onClearSelection
    );
  }
);
